import { Icon, V2Button } from '@sakhtar/design-system/react';
import { useApiGet } from '../../lib/useApiGet.js';
import { factorText, parseFactorInput, parsePriceInput } from '../../lib/productCatalog.js';
import type { CatalogUnit, MasterDataListResponse } from '../../lib/types/masterData.js';

/** A non-base sale unit while editing (strings as typed). */
export interface ExtraUnitDraft {
  uomId: string;
  /** Base units in one of this unit: "12" or "1/1000". */
  factor: string;
  price: string;
}

export interface UnitsDraft {
  basePrice: string;
  extra: ExtraUnitDraft[];
  /** Default sale unit; '' = the base unit. */
  defaultUomId: string;
}

export const EMPTY_UNITS_DRAFT: UnitsDraft = { basePrice: '', extra: [], defaultUomId: '' };

export function draftFromCatalogUnits(units: readonly CatalogUnit[]): UnitsDraft {
  const base = units.find((u) => u.isBase);
  const dflt = units.find((u) => u.isDefault && !u.isBase);
  return {
    basePrice: base?.unitPriceRial ?? '',
    defaultUomId: dflt?.uomId ?? '',
    extra: units
      .filter((u) => !u.isBase)
      .map((u) => ({
        uomId: u.uomId,
        factor: factorText(u.factorNumerator, u.factorDenominator),
        price: u.unitPriceRial ?? '',
      })),
  };
}

/** Validation message for the draft, or null when it can be sent. */
export function unitsDraftError(draft: UnitsDraft): string | null {
  if (parsePriceInput(draft.basePrice) === null) return 'قیمت واحد پایه باید عدد صحیح (ریال) باشد.';
  const seen = new Set<string>();
  for (const row of draft.extra) {
    if (!row.uomId) return 'برای هر ردیف واحد را انتخاب کنید.';
    if (seen.has(row.uomId)) return 'یک واحد را نمی‌توان دو بار ثبت کرد.';
    seen.add(row.uomId);
    if (!parseFactorInput(row.factor)) {
      return 'معادل هر واحد جانبی باید عدد صحیح مثبت یا کسر مانند 1/1000 باشد.';
    }
    if (parsePriceInput(row.price) === null) return 'قیمت واحد باید عدد صحیح (ریال) باشد.';
  }
  return null;
}

/** API `units` payload for a validated draft. */
export function unitsPayload(baseUomId: string, draft: UnitsDraft) {
  const price = (text: string) => {
    const parsed = parsePriceInput(text);
    return parsed === '' || parsed === null ? null : parsed;
  };
  return [
    {
      uomId: baseUomId,
      unitPriceRial: price(draft.basePrice),
      isDefault: draft.defaultUomId === '',
    },
    ...draft.extra.map((row) => {
      const factor = parseFactorInput(row.factor)!;
      return {
        uomId: row.uomId,
        factorNumerator: factor.numerator,
        factorDenominator: factor.denominator,
        unitPriceRial: price(row.price),
        isDefault: draft.defaultUomId === row.uomId,
      };
    }),
  ];
}

/**
 * Units a product is sold in: the base unit (factor 1) plus any number of
 * other units, each with "1 unit = N base units" and its own list price in
 * Rial. These feed the unit dropdown and the auto-filled price on quote lines.
 */
export function ProductUnitsEditor({
  base,
  baseUomId,
  value,
  onChange,
}: {
  readonly base: string;
  readonly baseUomId: string;
  readonly value: UnitsDraft;
  readonly onChange: (next: UnitsDraft) => void;
}) {
  const uoms = useApiGet<MasterDataListResponse>(
    `${base}/api/v1/master-data/uoms?status=active&limit=200`,
    [base],
  );
  const options = uoms.status === 'ready' ? uoms.data.items : [];
  const nameOf = (id: string) => {
    const row = options.find((u) => String(u.uomId) === id);
    return row ? String(row.name ?? row.code) : '…';
  };

  if (!baseUomId) {
    return (
      <span style={{ fontSize: 13, color: 'var(--sk-fg-muted)' }}>
        ابتدا «واحد سنجش پایه» را انتخاب کنید؛ سپس واحدها و قیمت‌ها را تعریف کنید.
      </span>
    );
  }

  const baseName = nameOf(baseUomId);
  const taken = new Set([baseUomId, ...value.extra.map((r) => r.uomId)]);
  const update = (index: number, patch: Partial<ExtraUnitDraft>) =>
    onChange({
      ...value,
      extra: value.extra.map((row, i) => (i === index ? { ...row, ...patch } : row)),
    });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div className="sk-table-scroll">
        <table className="sk-table sk-table--compact">
          <thead>
            <tr>
              <th style={{ minWidth: 140 }}>واحد</th>
              <th style={{ minWidth: 190 }}>معادل (به واحد پایه)</th>
              <th style={{ width: 150 }}>قیمت واحد (ریال)</th>
              <th style={{ width: 90 }}>پیش‌فرض</th>
              <th style={{ width: 40 }}></th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                {baseName} <span style={{ color: 'var(--sk-fg-subtle)' }}>(پایه)</span>
              </td>
              <td style={{ color: 'var(--sk-fg-subtle)' }}>
                ۱ {baseName} = ۱ {baseName}
              </td>
              <td>
                <input
                  className="sk-input ltr sk-num"
                  style={{ width: '100%' }}
                  inputMode="numeric"
                  value={value.basePrice}
                  onChange={(e) => onChange({ ...value, basePrice: e.target.value })}
                />
              </td>
              <td>
                <input
                  type="radio"
                  name="product-default-unit"
                  aria-label="واحد پیش‌فرض: واحد پایه"
                  checked={value.defaultUomId === ''}
                  onChange={() => onChange({ ...value, defaultUomId: '' })}
                />
              </td>
              <td></td>
            </tr>
            {value.extra.map((row, i) => (
              <tr key={i}>
                <td>
                  <select
                    className="sk-select"
                    style={{ width: '100%' }}
                    value={row.uomId}
                    onChange={(e) => update(i, { uomId: e.target.value })}
                  >
                    <option value="">— انتخاب واحد —</option>
                    {options.map((u) => {
                      const id = String(u.uomId);
                      return (
                        <option key={id} value={id} disabled={taken.has(id) && id !== row.uomId}>
                          {String(u.name ?? u.code)}
                        </option>
                      );
                    })}
                  </select>
                </td>
                <td>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ whiteSpace: 'nowrap' }}>
                      ۱ {row.uomId ? nameOf(row.uomId) : 'واحد'} =
                    </span>
                    <input
                      className="sk-input ltr sk-num"
                      style={{ width: 90 }}
                      inputMode="decimal"
                      placeholder="12"
                      aria-label="معادل به واحد پایه"
                      value={row.factor}
                      onChange={(e) => update(i, { factor: e.target.value })}
                    />
                    <span style={{ whiteSpace: 'nowrap' }}>{baseName}</span>
                  </div>
                </td>
                <td>
                  <input
                    className="sk-input ltr sk-num"
                    style={{ width: '100%' }}
                    inputMode="numeric"
                    value={row.price}
                    onChange={(e) => update(i, { price: e.target.value })}
                  />
                </td>
                <td>
                  <input
                    type="radio"
                    name="product-default-unit"
                    aria-label="واحد پیش‌فرض"
                    disabled={!row.uomId}
                    checked={row.uomId !== '' && value.defaultUomId === row.uomId}
                    onChange={() => onChange({ ...value, defaultUomId: row.uomId })}
                  />
                </td>
                <td>
                  <button
                    type="button"
                    className="sk-btn sk-btn--ghost sk-btn--icon sk-btn--sm"
                    style={{ color: 'var(--sk-danger)' }}
                    aria-label="حذف واحد"
                    onClick={() =>
                      onChange({
                        ...value,
                        defaultUomId: value.defaultUomId === row.uomId ? '' : value.defaultUomId,
                        extra: value.extra.filter((_, idx) => idx !== i),
                      })
                    }
                  >
                    <Icon name="x" size="sm" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <V2Button
          type="button"
          variant="secondary"
          size="sm"
          icon="plus"
          onClick={() =>
            onChange({ ...value, extra: [...value.extra, { uomId: '', factor: '', price: '' }] })
          }
        >
          افزودن واحد
        </V2Button>
        <span style={{ fontSize: 11, color: 'var(--sk-fg-subtle)' }}>
          در پیش‌فاکتور و فاکتور فقط همین واحدها برای کالا قابل انتخاب است و قیمت همان واحد خودکار
          می‌آید (قابل تغییر).
        </span>
      </div>
    </div>
  );
}
